// Phone-number formatting, per market.
//
// Formats free-typed input as the user types, in the shape that market writes
// its numbers: "(312) 456-7890" in the US and Canada, "+61 412 345 678" in
// Australia, "+44 7911 123456" in the UK. The grouping and the expected digit
// count both come from the country config, so adding a market is a config
// change rather than another branch in here.
//
// Two things make international input messier than NANP and are handled once,
// here, rather than at each call site:
//
//   - The trunk prefix. UK and AU numbers are written domestically with a
//     leading 0 (0412 345 678) which is dropped when the number is written
//     internationally (+61 412 345 678). Someone typing their own number will
//     type the 0 out of habit, so it is stripped rather than treated as the
//     first digit and pushing everything out of alignment.
//   - The dial code. A customer may paste "+61412345678", type "0061...", or
//     type the national number alone. All three mean the same number.

import { isValidPhoneNumber } from 'libphonenumber-js';
import type { CountryConfig, PhoneFormat } from '../config/countries';
import { countryConfigOrDefault } from '../config/countries';

/**
 * Reduce free-typed input to the national number's digits — no dial code, no
 * trunk prefix, capped at the market's expected length so the mask stays
 * stable when someone keeps typing.
 */
export function phoneNationalDigits(input: string, fmt: PhoneFormat): string {
  let d = (input || '').replace(/\D/g, '');
  if (d.startsWith('00')) d = d.slice(2);

  if (fmt.style === 'international') {
    // A dial code is only meaningful here when the value is long enough to be
    // a whole international number — a paste of "+61412345678". Anything
    // shorter is the customer's own digits and is left alone, including a
    // number that begins with the same digits as the dial code.
    // Exactly a complete international number — a paste of "+61412345678".
    // Testing "longer than a national number" instead would misfire on typing:
    // a tenth digit in a nine-digit market made the value look international
    // and silently removed the customer's first two digits.
    if (
      d.length === fmt.dialCode.length + fmt.nationalDigits &&
      d.startsWith(fmt.dialCode)
    ) {
      d = d.slice(fmt.dialCode.length);
    }
    // Domestic trunk prefix — the 0 in 07911 / 0412, dropped internationally.
    if (d.startsWith('0') && d.length > 1) d = d.slice(1);
  } else if (d.length === 11 && d.startsWith(fmt.dialCode)) {
    // NANP: a leading 1 is only a country code on a full eleven-digit entry.
    d = d.slice(fmt.dialCode.length);
  }

  return d.slice(0, fmt.nationalDigits);
}

/** Split digits into the market's groups, ignoring groups not yet reached. */
function group(digits: string, groups: number[]): string[] {
  const out: string[] = [];
  let i = 0;
  for (const size of groups) {
    if (i >= digits.length) break;
    out.push(digits.slice(i, i + size));
    i += size;
  }
  return out;
}

/**
 * Format as typed. Partial input formats partially — the field never rejects
 * or reorders what someone is midway through typing.
 */
export function formatPhone(input: string, country?: string | null): string {
  const fmt = countryConfigOrDefault(country).phone;
  const d = phoneNationalDigits(input, fmt);
  if (d.length === 0) return '';

  if (fmt.style === 'nanp') {
    if (d.length < 4) return `(${d}`;
    if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }

  return `+${fmt.dialCode} ${group(d, fmt.groups).join(' ')}`;
}

/**
 * Format a stored number for display — confirmation screens, summary rows,
 * anywhere the number is shown back rather than edited.
 *
 * Unlike formatPhone this does not format partial input: a number that is not
 * a complete national number is returned as given, because a half-formatted
 * phone number in a confirmation reads as corruption rather than as progress.
 */
export function formatPhoneForDisplay(input: string, country?: string | null): string {
  const raw = (input || '').trim();
  if (!raw) return '';
  const fmt = countryConfigOrDefault(country).phone;
  const d = phoneNationalDigits(raw, fmt);
  if (d.length !== fmt.nationalDigits) return raw;
  return formatPhone(d, country);
}

/** True when the input holds a complete national number for the market. */
export function isPhoneComplete(input: string, country?: string | null): boolean {
  const fmt = countryConfigOrDefault(country).phone;
  return phoneNationalDigits(input, fmt).length === fmt.nationalDigits;
}

/**
 * The value sent to the API.
 *
 * NANP markets keep exactly what production sends today — ten bare digits, no
 * country code — so US behaviour is unchanged to the byte. International
 * markets send dial code + national number, because a bare "412345678" is
 * ambiguous once numbers can originate outside North America.
 */
export function phoneForApi(input: string, country?: string | null): string {
  const fmt = countryConfigOrDefault(country).phone;
  const d = phoneNationalDigits(input, fmt);
  if (!d) return '';
  return fmt.style === 'nanp' ? d : `${fmt.dialCode}${d}`;
}

/**
 * What the input element itself shows.
 *
 * In international markets the dial code is rendered beside the field rather
 * than inside its value, so the input holds only the national number. Keeping
 * "+61 " in the value meant it came back on every keystroke mixed in with the
 * customer's digits, and no amount of parsing can reliably say which "61" is
 * which — typing the country code by hand doubled it, and stripping it
 * defensively wiped the field for anyone whose number began with those digits.
 * Out of the value, the ambiguity does not exist.
 */
export function phoneInputValue(stored: string, country?: string | null): string {
  const fmt = countryConfigOrDefault(country).phone;
  if (fmt.style === 'nanp') return stored;
  // The stored value is one we wrote, so its "+61 " prefix is removed as the
  // literal string it is. Re-parsing it as free input would fold the prefix's
  // digits back into the number.
  const prefix = `+${fmt.dialCode} `;
  const body = stored.startsWith(prefix) ? stored.slice(prefix.length) : stored;
  const d = phoneNationalDigits(body, fmt);
  return d ? group(d, fmt.groups).join(' ') : '';
}

/**
 * Format what was typed into the national-only input back into the value we
 * store and display, which keeps its dial code.
 */
export function formatPhoneFromInput(typed: string, country?: string | null): string {
  const fmt = countryConfigOrDefault(country).phone;
  if (fmt.style === 'nanp') return formatPhone(typed, country);
  const d = phoneNationalDigits(typed, fmt);
  return d ? `+${fmt.dialCode} ${group(d, fmt.groups).join(' ')}` : '';
}

/** The dial code shown beside the input, or null in NANP markets. */
export function phoneDialCodeLabel(country?: string | null): string | null {
  const fmt = countryConfigOrDefault(country).phone;
  return fmt.style === 'international' ? `+${fmt.dialCode}` : null;
}

/**
 * Tidy a studio's phone number for display.
 *
 * These are typed by hand into Salesforce and arrive in whatever shape the
 * person entering them used — "+61 2 9669-3200" mixes a hyphen into an
 * otherwise spaced international number, and spacing is inconsistent between
 * markets. Only presentation is touched: hyphens become spaces, runs of
 * whitespace collapse, and a bare international number gets its groups spaced.
 * The digits are never altered, because a phone number that has been
 * "corrected" is worse than an untidy one.
 */
export function formatStorePhone(raw: string, country?: string | null): string {
  const value = (raw || '').trim();
  if (!value) return '';

  const fmt = countryConfigOrDefault(country).phone;
  // The hyphen in "(317) 288-7103" is correct North American formatting and is
  // left alone. In international numbers it is a stray — "+61 2 9669-3200"
  // mixes conventions — so there it becomes a space.
  const tidy =
    fmt.style === 'international'
      ? value.replace(/[-–—]/g, ' ').replace(/\s+/g, ' ').trim()
      : value.replace(/\s+/g, ' ').trim();

  // Already grouped by whoever entered it — leave their grouping alone.
  if (tidy.includes(' ')) return tidy;

  // An unspaced international number, e.g. "+441256359865".
  if (tidy.startsWith('+')) {
    const digits = tidy.slice(1).replace(/\D/g, '');
    if (digits.startsWith(fmt.dialCode)) {
      const national = digits.slice(fmt.dialCode.length);
      if (national.length === fmt.nationalDigits) {
        return `+${fmt.dialCode} ${group(national, fmt.groups).join(' ')}`;
      }
    }
    return tidy;
  }

  const digits = tidy.replace(/\D/g, '');

  // An unspaced domestic number falls back to the market's own grouping.
  if (fmt.style === 'nanp' && digits.length === 10) return formatPhone(digits, country);

  // An international number stored WITHOUT its plus — "441293978944",
  // "610296696331". This is how Salesforce holds Store_Phone__c outside the
  // US, and every branch above misses it: no space, no leading plus, and not
  // ten digits. It fell through and rendered raw, which is what UK and
  // Australian testers saw on the confirmation screen.
  if (fmt.style === 'international' && digits.startsWith(fmt.dialCode)) {
    let national = digits.slice(fmt.dialCode.length);
    // The trunk zero survives the international prefix in some records —
    // "44" + "0" + national. It is not part of the number once the dial code
    // is present, so it comes off before grouping.
    if (national.startsWith('0') && national.length === fmt.nationalDigits + 1) {
      national = national.slice(1);
    }

    if (national.length === fmt.nationalDigits) {
      // Australian numbers group by kind, and every store is a landline.
      // Mobiles are 04xx xxx xxx, which is what the market config describes
      // because that is what customers type. Landlines are a one-digit area
      // code then eight digits — "+61 2 9669 6331" — and grouping one as the
      // other gives "+61 296 696 331", which is not a number anyone
      // recognises.
      if (fmt.dialCode === '61' && !national.startsWith('4')) {
        return `+61 ${national.slice(0, 1)} ${national.slice(1, 5)} ${national.slice(5)}`;
      }
      return `+${fmt.dialCode} ${group(national, fmt.groups).join(' ')}`;
    }
  }

  return tidy;
}

/** Placeholder for the market's phone input. */
export function phonePlaceholder(country?: string | null): string {
  const fmt = countryConfigOrDefault(country).phone;
  // Drop the dial code from the hint in markets that render it separately,
  // so the field does not appear to be asking for it twice.
  return fmt.style === 'international'
    ? fmt.placeholder.replace(`+${fmt.dialCode} `, '')
    : fmt.placeholder;
}

/** Whether the market's config is available — re-exported for call sites that
 * already hold a CountryConfig and want to avoid a second lookup. */
export function phoneFormatOf(config: CountryConfig): PhoneFormat {
  return config.phone;
}

/* ── Edited dial codes ──────────────────────────────────────────────────────
 *
 * Where a market allows the dial code to be changed (UK), a customer can enter
 * a number that is not from this market at all. Everything above is built on
 * the market's own grouping and digit count, and neither applies to +353 or
 * +1 — so once the code has actually been changed these take over.
 *
 * They deliberately do LESS than the market path. There is no grouping table
 * for arbitrary countries and no expected length, so the digits are shown as
 * typed and validity comes from libphonenumber rather than a digit count.
 * Guessing a format for a country we know nothing about would corrupt input
 * that was correct; showing it plainly cannot.
 *
 * `null` or the market's own code means nothing was overridden, and every one
 * of these falls through to the existing behaviour byte for byte.
 */

/** The code in force: the customer's, or the market's if they left it alone. */
export function effectiveDialCode(
  country?: string | null,
  override?: string | null,
): string {
  const fmt = countryConfigOrDefault(country).phone;
  return normalizeDialCode(override) || fmt.dialCode;
}

/** True when the customer has moved the code off the market's own. */
export function isDialCodeOverridden(
  country?: string | null,
  override?: string | null,
): boolean {
  const code = normalizeDialCode(override);
  return code !== '' && code !== countryConfigOrDefault(country).phone.dialCode;
}

/** Digits of a dial code, no plus, no spaces. Empty when there is nothing. */
export function normalizeDialCode(raw?: string | null): string {
  return (raw || '').replace(/\D/g, '').slice(0, 4);
}

/**
 * Digits of a foreign number, as typed.
 *
 * The only things removed are an international access prefix and the dial code
 * when the whole thing was pasted — never a leading zero. Most countries drop
 * their trunk 0 internationally but Italy keeps it, and there is no way to
 * know which we are looking at. Stripping it would silently delete a real
 * digit from an Italian number; leaving it costs a customer one keystroke.
 */
function foreignNationalDigits(input: string, dialCode: string): string {
  let d = (input || '').replace(/\D/g, '');
  if (d.startsWith('00')) d = d.slice(2);
  // Only for a paste long enough to be a whole international number, the same
  // rule the market path uses, for the same reason.
  if (d.length > dialCode.length + 4 && d.startsWith(dialCode)) {
    d = d.slice(dialCode.length);
  }
  // E.164 caps the whole number at 15 digits including the code.
  return d.slice(0, Math.max(0, 15 - dialCode.length));
}

/** What a foreign-coded input shows: the digits, ungrouped. */
export function foreignInputValue(stored: string, dialCode: string): string {
  const prefix = `+${dialCode} `;
  const body = stored.startsWith(prefix) ? stored.slice(prefix.length) : stored;
  return foreignNationalDigits(body, dialCode);
}

/** Store-and-display form of a foreign number. */
export function formatForeignFromInput(typed: string, dialCode: string): string {
  const d = foreignNationalDigits(typed, dialCode);
  return d ? `+${dialCode} ${d}` : '';
}

/** Complete per libphonenumber, which is the only authority we have here. */
export function isForeignPhoneComplete(input: string, dialCode: string): boolean {
  const d = foreignNationalDigits(input, dialCode);
  if (!d) return false;
  try {
    return isValidPhoneNumber(`+${dialCode}${d}`);
  } catch {
    return false;
  }
}

/** API form — dial code + national digits, matching the international path. */
export function foreignPhoneForApi(input: string, dialCode: string): string {
  const d = foreignNationalDigits(input, dialCode);
  return d ? `${dialCode}${d}` : '';
}

/**
 * Whether the stored phone is a complete number, honouring an edited dial
 * code where the market allows one.
 *
 * The one place the two validation paths meet. Step 1 and the Confirm step
 * both gate on this, so a number that passes on one screen cannot fail on
 * the other, and a market that later allows an editable code picks it up
 * without touching either screen.
 */
export function phoneComplete(
  input: string,
  country?: string | null,
  dialCodeOverride?: string | null,
): boolean {
  return isDialCodeOverridden(country, dialCodeOverride)
    ? isForeignPhoneComplete(input, effectiveDialCode(country, dialCodeOverride))
    : isPhoneComplete(input, country);
}

/**
 * The phone as the booking API wants it — digits only, with the dial code in
 * front outside North America — or undefined when there is none, so the
 * payload leaves the field out rather than sending an empty string that
 * Salesforce would write over whatever the account already holds.
 */
export function phoneForBookingApi(
  input: string,
  country?: string | null,
  dialCodeOverride?: string | null,
): string | undefined {
  if (!input.trim()) return undefined;
  const v = isDialCodeOverridden(country, dialCodeOverride)
    ? foreignPhoneForApi(input, effectiveDialCode(country, dialCodeOverride))
    : phoneForApi(input, country);
  return v || undefined;
}
